// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect } from 'react'
import type { NodeResponse } from 'models-ts/node'
import { useMaskDrawing } from './useMaskDrawing'
import { useBrushModeIntegration } from './useBrushModeIntegration'
import { useAppDispatch } from '../../../../store/hooks/hooks'
import {
    setBrushEnabled as setBrushEnabledGlobal,
    setHasMaskStrokes,
} from '../../../../store/slices/selectionSlice'

interface ImageSize {
    w: number
    h: number
}

interface LoraBrushModeProps {
    containerRef: React.RefObject<HTMLDivElement | null>
    contentScale: number
    contentOffset: { x: number; y: number }
    imageNaturalSize: ImageSize | null
    imageRectInContainer: { x: number; y: number; w: number; h: number } | null
    session: { id: string; nodes: NodeResponse[] } | null
    selected: string[]
    globalBrushEnabled: boolean
    lorasState: { loading: boolean; items: Array<unknown> }
    setMaskActions: (actions: any) => void
    exportFramedImageWithUpload: () => Promise<{ node: any; commit: any } | null>
    setContentScale: (scale: number) => void
    setContentOffset: (offset: { x: number; y: number }) => void
}

export interface LoraBrushModeResult {
    brushEnabled: boolean
    setBrushEnabled: (v: boolean) => void
    activeMaskItem: { id: string; src: string | null } | null
    maskStageProps: {
        stageRef: React.RefObject<any>
        maskLayerRef: React.RefObject<any>
        uiLayerRef: React.RefObject<any>
        maskContentGroupRef: React.RefObject<any>
        uiContentGroupRef: React.RefObject<any>
        containerSize: { w: number; h: number }
        imageRectInContainer: { x: number; y: number; w: number; h: number } | null
        showBrush: boolean
        brushCenter: { x: number; y: number } | null
        brushDiameterCssPx: number
        brushDiameterWorldPx: number
        brushEnabled: boolean
        contentScale: number
        contentOffset: { x: number; y: number }
        hasActiveImage: boolean
        handlers: {
            onPointerDown: (e: any) => void
            onPointerMove: (e: any) => void
            onPointerUp: () => void
            onPointerLeave: () => void
        }
    }
    brushToolbarProps: {
        brushSizeCssPx: number
        canUndo: boolean
        canRedo: boolean
        onBrushSizeChange: (nextCssPx: number) => void
        onUndo: () => void
        onRedo: () => void
    }
    handleEnableBrush: () => void
    handleExitBrush: () => void
}

export function useLoraBrushMode(props: LoraBrushModeProps): LoraBrushModeResult {
    const {
        containerRef,
        contentScale,
        contentOffset,
        imageNaturalSize,
        imageRectInContainer,
        session,
        selected,
        globalBrushEnabled,
        lorasState,
        setMaskActions,
        exportFramedImageWithUpload,
        setContentScale,
        setContentOffset,
    } = props

    const dispatch = useAppDispatch()

    const {
        stageRef,
        maskLayerRef,
        uiLayerRef,
        maskContentGroupRef,
        uiContentGroupRef,
        containerSize,
        imageRectInContainer: maskImageRect,
        brushEnabled,
        setBrushEnabled,
        brushDiameterCssPx,
        brushDiameterWorldPx,
        setBrushRadiusImagePx,
        showBrush,
        brushCenter,
        canUndo,
        canRedo,
        hasStrokes,
        maskHandlers,
        maskActions,
    } = useMaskDrawing(imageNaturalSize, containerRef, contentScale, contentOffset)

    useBrushModeIntegration({
        brushEnabled,
        lorasState,
        setMaskActions,
        maskActions,
        exportFramedImageWithUpload,
        setBrushEnabled,
    })

    const activeMaskItem = (() => {
        if (!session?.nodes || selected.length === 0) return null
        const lastId = selected[selected.length - 1]
        const node = session.nodes.find((n: NodeResponse) => n?.id === lastId)
        const src = node?.image_path ?? null
        return { id: lastId, src }
    })()

    const handleEnableBrush = useCallback(() => {
        if (!session?.id) return
        dispatch(setBrushEnabledGlobal({ sessionId: session.id, brushEnabled: true }))
        window.dispatchEvent(
            new CustomEvent('canvas:enablebrush', { detail: { navigate: 'lora' } })
        )
    }, [dispatch, session?.id])

    const handleExitBrush = useCallback(() => {
        if (!session?.id) return
        dispatch(setBrushEnabledGlobal({ sessionId: session.id, brushEnabled: false }))
        window.dispatchEvent(new CustomEvent('canvas:exitbrush'))
    }, [dispatch, session?.id])

    useEffect(() => {
        if (!session?.id) return
        dispatch(setBrushEnabledGlobal({ sessionId: session.id, brushEnabled: Boolean(brushEnabled) }))
    }, [brushEnabled, dispatch, session?.id])

    useEffect(() => {
        if (globalBrushEnabled !== brushEnabled) {
            setBrushEnabled(globalBrushEnabled ?? false)
        }
    }, [globalBrushEnabled, brushEnabled, setBrushEnabled])

    useEffect(() => {
        if (!session?.id) return
        dispatch(setHasMaskStrokes({ sessionId: session.id, hasMaskStrokes: Boolean(hasStrokes) }))
    }, [hasStrokes, dispatch, session?.id])

    useEffect(() => {
        if (!brushEnabled) return
        setContentScale(1)
        setContentOffset({ x: 0, y: 0 })
    }, [brushEnabled, setContentScale, setContentOffset])

    const handleBrushSizeChange = useCallback(
        (nextCssPx: number) => {
            if (!imageNaturalSize || !imageRectInContainer) return
            const scaleX = imageRectInContainer.w / imageNaturalSize.w
            const nextRadiusImagePx = Math.max(1, nextCssPx / 2 / (scaleX || 1))
            setBrushRadiusImagePx(nextRadiusImagePx)
        },
        [imageNaturalSize, imageRectInContainer, setBrushRadiusImagePx]
    )

    return {
        brushEnabled,
        setBrushEnabled,
        activeMaskItem,
        maskStageProps: {
            stageRef,
            maskLayerRef,
            uiLayerRef,
            maskContentGroupRef,
            uiContentGroupRef,
            containerSize,
            imageRectInContainer: maskImageRect,
            showBrush,
            brushCenter,
            brushDiameterCssPx,
            brushDiameterWorldPx,
            brushEnabled,
            contentScale,
            contentOffset,
            hasActiveImage: !!activeMaskItem?.src,
            handlers: maskHandlers,
        },
        brushToolbarProps: {
            brushSizeCssPx: brushDiameterCssPx,
            canUndo,
            canRedo,
            onBrushSizeChange: handleBrushSizeChange,
            onUndo: maskActions.undo,
            onRedo: maskActions.redo,
        },
        handleEnableBrush,
        handleExitBrush,
    }
}

